import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { node_server } from "../constants";
import { setIsButtonLoading } from "../store/slices/loadingSlice";
import {
	setData,
	setProjectFavorites,
} from "../store/slices/profile/discoverSlice";
import {
	setIsDrawerOpen,
	setLikedProjects,
	setProjectApplicants,
	setProjects,
	updateManageProjectsData,
} from "../store/slices/profile/manageProjectsSlice";
import {
	setPublicHostedProjects,
	setPublicLikedProjects,
} from "../store/slices/profile/publicUserSlice";

function useManageProjects() {
	const user = useSelector((state) => state.user.value);
	const { data, projectFavorites } = useSelector((state) => state.discover);
	// useDiscover(true);
	const dispatch = useDispatch();
	const { pathname } = useLocation();
	const {
		data: { applications, hostedProjects, likedProjects },
	} = useSelector((state) => state.manageProjects);
	const isPublic = pathname.includes("creators");

	const toggleDrawer = (newOpen) => () => {
		dispatch(setIsDrawerOpen(newOpen));
	};

	const handleFavorite = async (id, state) => {
		if (state === "add") {
			const { data: newFav, error } = await axios.post(
				`${node_server}/api/projects/favourite`,
				{
					projectId: id,
					userId: user?.id,
				},
				{ withCredentials: true },
			);

			if (error) {
				console.log(error);
			} else {
				dispatch(setProjectFavorites([...projectFavorites, newFav[0]]));
				dispatch(
					setData(
						data.map((item) =>
							item.project_id === id
								? { ...item, is_favourite: true }
								: item,
						),
					),
				);
			}
		} else if (state === "remove") {
			const { error } = await axios.delete(
				`${node_server}/api/projects/favourite?projectId=${id}`,
				{
					withCredentials: true,
				},
			);

			if (error) {
				console.log(error);
			} else {
				dispatch(
					setProjectFavorites(
						projectFavorites.filter(
							(item) => item.project_id !== id,
						),
					),
				);
				dispatch(
					setData(
						data.map((item) =>
							item.project_id === id
								? { ...item, is_favourite: false }
								: item,
						),
					),
				);
			}
		}
	};

	const fetchFavorite = async (id) => {
		if (user && user?.id) {
			const { data, error } = await axios.get(
				`${node_server}/api/projects/favourite/${id}`,
				{
					withCredentials: true,
				},
			);

			if (error) console.log(error);

			return data.length > 0;
		}
		return false;
	};

	const handleDeleteApplicants = async (item) => {
		const response = await axios.delete(
			`${node_server}/api/projects/applications`,
			{
				projectId: item.project_id,
			},
			{ withCredentials: true },
		);

		if (response.status !== 200) {
			console.log(response.data);
		}
	};

	const handleRemoveApplication = async (project) => {
		const response = await axios.delete(
			`${node_server}/api/projects/applications`,
			{
				projectId: project.project_id,
			},
			{ withCredentials: true },
		);

		if (response.status !== 200) {
			console.log(response.data);
		}
	};

	const handleProjectDelete = async (project) => {
		const response = await axios.put(
			`${node_server}/api/projects/details/${project.id}`,
			{ status: "deleted" },
			{ withCredentials: true },
		);

		if (response.status !== 200) {
			console.log(response.data);
		}
		// await sendProjectDeletedemail(project.id);
	};

	const fetchProjectApplicants = async (project) => {
		if (project) {
			const response = await axios.get(
				`${node_server}/api/projects/applications-with-user-info?projectId=${project.id}`,
				{
					withCredentials: true,
				},
			);

			if (response.status !== 200) console.log(response.data);
			if (response.data)
				dispatch(setProjectApplicants(response.data || []));
		}
	};

	const fetchLikedProjects = async (user) => {
		const { data, error } = await axios.post(
			`${node_server}/api/user/favorite-projects`,
			{
				userId: user?.id,
			},
			{
				withCredentials: true,
			},
		);
		if (error) console.log(error);

		if (isPublic) {
			const response = await axios.get(
				`${node_server}/api/projects/with-status`,
				{
					params: { status: "Under Review" },
					withCredentials: true,
				},
			);
			const pendingProjects = response.data;
			const error = response.status !== 200 ? response.data : null;

			if (error) console.log(error);
			if (pendingProjects) {
				const pendingProjectsIds = pendingProjects.map(
					(pend) => pend.id,
				);
				const filteredData = data.filter(
					(item) => !pendingProjectsIds.includes(item.project_id),
				);
				dispatch(setPublicLikedProjects(filteredData));
			}
		} else {
			dispatch(setLikedProjects(data));
		}
	};

	const fetchHostedProjects = async (user) => {
		if (isPublic) {
			const response = await axios.get(
				`${node_server}/api/projects/with-status`,
				{
					params: { status: "Completed,Accepted,Approved" },
					withCredentials: true,
				},
			);
			const data = response.data;
			const error = response.status !== 200 ? response.data : null;

			if (error) console.log(error);
			if (data) dispatch(setPublicHostedProjects(data));
		} else {
			// const { data, error } = await supabase
			//   .from("projects")
			//   .select()
			//   .eq("user_id", user?.id)
			//   .neq("status", "deleted");
			if (hostedProjects.length > 0) return;
			const { data, error } = await axios.get(
				`${node_server}/api/projects/hosted`,
				{ withCredentials: true },
			);

			if (error) console.log(error);
			if (data)
				dispatch(updateManageProjectsData({ hostedProjects: data }));
		}
	};

	const fetchProjects = async () => {
		const response = await axios.get(
			`${node_server}/api/projects/info-with-total-applications`,
			{
				withCredentials: true,
			},
		);
		const data = response.data;
		const error = response.status !== 200 ? response.data : null;

		if (error) console.error("Error fetching projects:", error.message);

		if (data) {
			const filteredData = data.filter(
				(project) => project.status !== "deleted",
			);
			dispatch(setProjects(filteredData));
		}
	};

	const fetchApplications = async () => {
		// const { data, error } = await supabase
		//   .from("project_applications_with_project_info")
		//   .select("*")
		//   .eq("user_id", user?.id);
		if (Array.isArray(applications) && applications.length > 0) return;
		const { data, error } = await axios.get(
			`${node_server}/api/projects/private/view`,
			{ withCredentials: true },
		);

		if (error) console.log(error);
		if (data)
			dispatch(
				updateManageProjectsData({ applications: data.applications }),
			);
	};

	const fetchProjectsViewData = async () => {
		if (
			hostedProjects === null ||
			applications === null ||
			likedProjects === null
		) {
			dispatch(setIsButtonLoading(true));
			const { data, error } = await axios.get(
				`${node_server}/api/projects/private/view`,
				{
					withCredentials: true,
				},
			);
			if (error) console.log(error);
			if (data)
				dispatch(
					updateManageProjectsData({
						hostedProjects: data.hostedProjects,
						applications: data.applications,
						likedProjects: data.likedProjects,
					}),
				);
			dispatch(setIsButtonLoading(false));
		}
	};

	return {
		toggleDrawer,
		handleFavorite,
		fetchFavorite,
		handleDeleteApplicants,
		handleRemoveApplication,
		handleProjectDelete,
		fetchProjectApplicants,
		fetchLikedProjects,
		fetchHostedProjects,
		fetchProjects,
		fetchApplications,
		fetchProjectsViewData,
	};
}

export default useManageProjects;
